import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import QRCode from 'react-qr-code';
import { TranslationService } from '../../../../services/translation.service';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import bankidLogoIcon from '../../../../../assets/icons/bankid-logo.svg';
import { LanguageService } from '../../../../services/language.service';
import { SignResponseApiModel } from './models/sign-response.api-model';
import type { ISignResponse } from '../../../../models/sign-response';
import { SignFault, SignFaultHelper } from '../../../../models/sign-fault.enum';
import type { ISignFault } from '../../../../models/sign-fault.model';
import type { ISignCollectResponse } from '../../../../models/sign-collect-response';
import { SignCollectStatus } from '../../../../models/sign-collect-status.enum';
import { SignCollectResponseApiModel } from './models/sign-collect-response.api-model';
import Spinner from '../../../Shared/Spinner';
import FocusTrap from '../../../Shared/FocusTrap';
import { SignHelper } from './helpers/sign.helper';
import DeviceHelper, { DeviceType } from '../../../../helpers/device.helper';

enum SignStep {
  none = 'none',
  initial = 'initial',
  sign = 'sign',
  reSign = 'resign',
  collect = 'collect',
  done = 'done',
  error = 'error',
  cancel = 'cancel',
}

enum ActiveDevice {
  none = 'none',
  same = 'same',
  other = 'other',
}

function isMobileDevice(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = window.navigator as any;
  const userAgentDataIsMobile = nav.userAgentData?.mobile;
  return typeof userAgentDataIsMobile === 'boolean' ? userAgentDataIsMobile : typeof window.orientation !== 'undefined' || navigator.userAgent.indexOf('IEMobile') !== -1;
}

function removeRedirectHash(redirectHash: string): void {
  if (window.location.hash.indexOf(redirectHash)) {
    window.location.hash = '';
  }
}

function Sign({
  show,
  contentGuid,
  personalIdentityNumber,
  signComplete,
}: {
  show: boolean;
  contentGuid: string;
  personalIdentityNumber: string;
  signComplete: (success: boolean, transactionId: string | null, orderRef: string | null) => void;
}) {
  const [currentSignStep, setCurrentSignStep] = useState(SignStep.none);
  const [signResponse, setSignResponse] = useState<ISignResponse | null>(null);
  const [signFault, setSignFault] = useState<SignFault>(SignFault.none);
  const [statusText, setStatusText] = useState('');
  const [activeDevice, setActiveDevice] = useState(isMobileDevice() ? ActiveDevice.same : ActiveDevice.other);
  const [lastCollectTime, setLastCollectTime] = useState<DateTime | null>(null);
  const [lastSignTime, setLastSignTime] = useState<DateTime | null>(null);
  const [signCount, setSignCount] = useState<number>(0);
  const [collectStatus, setCollectStatus] = useState<SignCollectStatus | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentSignStepRef = useRef(currentSignStep);
  const signResponseRef = useRef(signResponse);

  const maximumSignAttempts = 6;

  const browser = DeviceHelper.getBrowser();
  const redirectHash = 'redirect';
  const isSigningActive = currentSignStep === SignStep.initial || currentSignStep === SignStep.collect || currentSignStep === SignStep.reSign || currentSignStep === SignStep.sign;

  function showMessage(message: string) {
    if (message.indexOf('rfa') === 0) {
      setStatusText(decodeURIComponent(TranslationService.get(`delaycompensationformpage.${message}`)));
    } else {
      setStatusText(message);
    }
  }

  const handleFault = useCallback(
    (fault: ISignFault) => {
      setCurrentSignStep(SignStep.error);
      setQrCode(null);
      const errorCode = fault?.errorCode;
      if (errorCode === SignFault.expiredtransaction) {
        showMessage('rfa8');
      } else if (errorCode === SignFault.certificateerror) {
        showMessage('rfa16');
      } else if (errorCode === SignFault.usercancel) {
        showMessage('rfa6');
      } else if (errorCode === SignFault.cancelled) {
        showMessage('rfa3');
      } else if (errorCode === SignFault.alreadyinprogress) {
        showMessage('rfa4');
      } else if (errorCode === SignFault.startfailed) {
        if (signCount > 1) showMessage(`rfa17${activeDevice === ActiveDevice.same ? 'a' : 'b'}`);
      } else {
        showMessage('rfa22');
      }
      setSignFault(errorCode);
      setSignResponse(null);
    },
    [activeDevice, signCount]
  );

  const autostartBankId = useCallback(
    (result: ISignResponse) => {
      function getIosRedirectUrl(): string {
        if (browser.name === 'ios') {
          // safari
          return encodeURI(`${window.location.href}#${redirectHash}`);
        }
        if (browser.name === 'crios') {
          return encodeURI('googlechromes://');
        }
        if (browser.name === 'fxios') {
          return encodeURI('firefox://');
        }
        return 'null';
      }

      if (isMobileDevice()) {
        if (browser.deviceType === DeviceType.Android) {
          if (browser.name === 'chrome') {
            window.location.href = `https://app.bankid.com/?autostarttoken=${result.autoStartToken}&redirect=null`;
          } else {
            window.location.href = `bankid:///?autostarttoken=${result.autoStartToken}&redirect=null`;
          }
        } else if (browser.deviceType === DeviceType.iOS) {
          const redirectUrl = getIosRedirectUrl();
          window.location.href = `https://app.bankid.com/?autostarttoken=${result.autoStartToken}&redirect=${redirectUrl}`;
        } else {
          window.location.href = `https://app.bankid.com/?autostarttoken=${result.autoStartToken}&redirect=null`;
        }
      } else {
        // Desktop
        window.location.href = `bankid:///?autostarttoken=${result.autoStartToken}&redirect=null`;
      }
    },
    [browser.name, browser.deviceType]
  );

  useEffect(() => {
    document.body.classList.toggle('modal-open', show);
  }, [show]);

  useEffect(() => {
    currentSignStepRef.current = currentSignStep;
  }, [currentSignStep]);

  useEffect(() => {
    signResponseRef.current = signResponse;
  }, [signResponse]);

  useEffect(() => {
    function handleSignCollectResponse(response: ISignCollectResponse) {
      setLastCollectTime(DateTime.now());
      const status = response?.status;
      setCollectStatus(status);
      setQrCode(status === SignCollectStatus.outstandingtransaction ? response.qrCode ?? null : null);
      if (status === SignCollectStatus.outstandingtransaction) {
        setCurrentSignStep(SignStep.collect);
        if (activeDevice === ActiveDevice.same) {
          showMessage('rfa13');
        }
      } else if (status === SignCollectStatus.noclient) {
        setCurrentSignStep(SignStep.collect);
        showMessage('rfa1');
      } else if (status === SignCollectStatus.started) {
        setCurrentSignStep(SignStep.collect);
        showMessage(`rfa15${activeDevice === ActiveDevice.same ? 'a' : 'b'}`);
      } else if (status === SignCollectStatus.usersign) {
        setCurrentSignStep(SignStep.collect);
        showMessage('rfa9');
      } else if (status === SignCollectStatus.complete) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setCurrentSignStep(SignStep.done);
        setStatusText('');
        signComplete(true, signResponse!.transactionId, signResponse!.orderRef);
      } else {
        showMessage('rfa21');
        setCurrentSignStep(SignStep.initial);
        showMessage(status);
      }
    }

    async function sign(reSign: boolean) {
      if (!reSign) {
        setCurrentSignStep(SignStep.initial);
      } else {
        setCurrentSignStep(SignStep.reSign);
      }
      setLastSignTime(DateTime.now());
      setSignCount(signCount + 1);
      try {
        const response = await fetch(`/api/digitalidentity/v2/sign?guid=${contentGuid}&personalIdentityNumber=${personalIdentityNumber}&language=${LanguageService.getCurrentLanguage()}`);
        if (currentSignStepRef.current === SignStep.cancel || currentSignStepRef.current === SignStep.none) return;
        if (!response.ok) throw new Error(await response.json());
        const result = SignResponseApiModel.mapToBusinessModel(await response.json());
        setSignResponse(result);
        setQrCode(result.qrCode);
        setCurrentSignStep(SignStep.collect);
        if (activeDevice === ActiveDevice.same) {
          autostartBankId(result);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const fault: ISignFault = { errorCode: SignFaultHelper.parse(error.message) };
        handleFault(fault);
      }
    }

    async function collect() {
      if (signResponseRef.current === null) throw new Error('No sign response when calling collect');
      try {
        const response = await fetch('/api/digitalidentity/sign/collect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactionId: signResponseRef.current.transactionId, orderRef: signResponseRef.current.orderRef }),
        });
        if (currentSignStepRef.current === SignStep.cancel || currentSignStepRef.current === SignStep.none) return;
        if (!response.ok) throw new Error(await response.json());
        const result = SignCollectResponseApiModel.mapToBusinessModel(await response.json());
        handleSignCollectResponse(result);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const fault: ISignFault = { errorCode: SignFaultHelper.parse(error.message) };
        if (fault?.errorCode === SignFault.startfailed && signCount <= maximumSignAttempts) sign(true);
        else handleFault(fault);
      }
    }
    if (show) {
      const shouldStartSign = currentSignStep === SignStep.none;
      if (shouldStartSign) {
        sign(false);
      }
      if (SignHelper.signAndCollectTimesExpired(lastSignTime, lastCollectTime, DateTime.now())) {
        setCurrentSignStep(SignStep.error);
        showMessage('rfa22');
        setSignResponse(null);
        setQrCode(null);
      } else {
        const shouldStartCollect = currentSignStep === SignStep.collect && timeoutRef.current === null;
        if (shouldStartCollect) {
          timeoutRef.current = setTimeout(async () => {
            timeoutRef.current = null;
            removeRedirectHash(redirectHash);
            await collect();
          }, 1000);
        }
      }
    }
  }, [activeDevice, currentSignStep, handleFault, signComplete, signResponse, show, contentGuid, personalIdentityNumber, lastSignTime, signCount, lastCollectTime, signFault, autostartBankId]);

  async function cancelIfNecessary() {
    setCurrentSignStep(SignStep.cancel);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if ((currentSignStep === SignStep.initial || currentSignStep === SignStep.collect) && signResponse && signResponse.transactionId && signResponse.orderRef) {
      try {
        await fetch('/api/digitalidentity/sign/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactionId: signResponse.transactionId, orderRef: signResponse.orderRef }),
        });
      } catch {
        // Ignore
      }
    }
  }

  function reset(): void {
    setQrCode(null);
    setActiveDevice(isMobileDevice() ? ActiveDevice.same : ActiveDevice.other);
    setSignResponse(null);
    setStatusText('');
    setLastCollectTime(null);
    setLastSignTime(null);
    setSignCount(0);
    setSignFault(SignFault.none);
    setCurrentSignStep(SignStep.none);
  }

  function handleOnClose() {
    cancelIfNecessary();
    reset();
    signComplete(false, null, null);
  }

  function instructionsOtherDevice(): JSX.Element {
    return (
      <div className="d-flex justify-content-center">
        <ol>
          <li className="text-start">{TranslationService.get('delaycompensationformpage.instructions.otherdevice.1')}</li>
          <li className="text-start">{TranslationService.get('delaycompensationformpage.instructions.otherdevice.2')}</li>
          <li className="text-start">{TranslationService.get('delaycompensationformpage.instructions.otherdevice.3')}</li>
          <li className="text-start">{TranslationService.get('delaycompensationformpage.instructions.otherdevice.4')}</li>
        </ol>
      </div>
    );
  }

  return (
    <div>
      {show && (
        <FocusTrap isVisible={show}>
          <div className="modal d-block fade show" aria-labelledby="sign-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h2 id="sign-modal-title" className="modal-title h5">
                    {TranslationService.get('delaycompensationformpage.signwithbankid')}
                  </h2>
                  <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
                </div>
                <div className="modal-body py-3 text-center">
                  {isSigningActive && !qrCode && <img src={DataUrlHelper.encode(bankidLogoIcon)} alt="Bank ID-logga" className="d-inline-block w-50" />}
                  {activeDevice === ActiveDevice.other && qrCode && <QRCode value={qrCode} className="mb-3" />}
                  {activeDevice === ActiveDevice.other && currentSignStep !== SignStep.error && !qrCode && !collectStatus && <Spinner className="mb-3" />}
                  {statusText && <div className={`text-center ${isSigningActive ? 'mb-3' : ''}`}>{statusText}</div>}
                  {isSigningActive && activeDevice === ActiveDevice.other && qrCode && instructionsOtherDevice()}
                  {activeDevice !== ActiveDevice.none &&
                    (currentSignStep === SignStep.collect || currentSignStep === SignStep.reSign) &&
                    collectStatus === SignCollectStatus.outstandingtransaction && (
                      <div className="w-100">
                        {activeDevice === ActiveDevice.same && (
                          <div className="w-100 mb-3 pb-3 border-bottom">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => {
                                if (!signResponse) throw new Error('No sign response when clicking open button');
                                autostartBankId(signResponse);
                              }}
                            >
                              {TranslationService.get('delaycompensationformpage.openbankid')}
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn--as-link"
                          onClick={() => {
                            if (activeDevice === ActiveDevice.other) {
                              if (!signResponse) throw new Error('No sign response when clicking other device button');
                              autostartBankId(signResponse);
                              setActiveDevice(ActiveDevice.same);
                            } else if (activeDevice === ActiveDevice.same) {
                              setActiveDevice(ActiveDevice.other);
                            }
                          }}
                        >
                          {activeDevice === ActiveDevice.same
                            ? TranslationService.get('delaycompensationformpage.bankidonanotherdevice')
                            : TranslationService.get('delaycompensationformpage.bankidonthisdevice')}
                        </button>
                      </div>
                    )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleOnClose}>
                    {TranslationService.get('delaycompensationformpage.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
      {show && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}

export default Sign;
